'use client';

import React from 'react';
import { AssessmentResult, AssessmentConfig, Question } from './types';
import { QuizItem } from '@/store/assessment-store';
import { useResultsNavigation } from './hooks/useResultsNavigation';
import { ResultsHeader } from './components/ResultsHeader';
import { ResultsSummary } from './components/ResultsSummary';
import { QuestionReview } from './components/QuestionReview';

interface QuizResultsProps {
  result: AssessmentResult;
  assessment: AssessmentConfig;
  assessmentName: string;
  learningPathUrl: string;
  updatedAt: string;
  totalQuestions: number;
  questions?: Question[];
  quiz?: QuizItem[];
  quizData?: any;
  firstCourse?: {
    title: string;
    uid: string;
  };
  courseProgressPercentage?: number;
  isStandaloneCourse?: boolean;
  /** When true (e.g. revisit/refresh after submit): show only score card, hide questionnaires and quiz progress bar */
  scoreCardOnly?: boolean;
  /** When provided, shown in header as "Completed: {date}" next to Released */
  completedAt?: string | null;
  /** When provided with onQuestionIndexChange, question index is controlled by parent (e.g. for sidebar sync) */
  controlledQuestionIndex?: number;
  onQuestionIndexChange?: (index: number) => void;
  onRetake: () => void;
  onNextChapter?: () => void;
  /** Label for next chapter button (e.g. "Next chapter" or "Back to course") */
  nextChapterLabel?: string;
}

export function QuizResults({
  result,
  assessment,
  assessmentName,
  learningPathUrl,
  updatedAt,
  totalQuestions,
  questions = [],
  quiz = [],
  quizData,
  firstCourse,
  courseProgressPercentage = 0,
  isStandaloneCourse = false,
  scoreCardOnly = false,
  completedAt,
  controlledQuestionIndex,
  onQuestionIndexChange,
  onRetake,
  onNextChapter,
  nextChapterLabel = 'Next chapter',
}: QuizResultsProps) {
  const totalMarks = result?.totalMarks ?? result?.attemptLog?.at(-1)?.totalMarks ?? 0;
  const totalQuestionsCount = result?.totalQuestions ?? result?.attemptLog?.at(-1)?.totalQuestions ?? totalQuestions;
  const scorePercentage =
    result?.percentageAchieved ??
    (totalQuestionsCount > 0 ? Math.round((totalMarks / totalQuestionsCount) * 100) : 0);
  const quizProgressPercentage = 100;

  const {
    questionIndex,
    setQuestionIndex,
    currentQuestion,
    questionType,
    correctAnswers,
  } = useResultsNavigation({
    questions,
    quiz,
    quizData,
    result,
    controlledQuestionIndex,
    onQuestionIndexChange,
  });

  return (
    <div className="w-full flex flex-col">
      <div className="flex-1 flex flex-col">
        <ResultsHeader
          title={assessmentName}
          type="quiz"
          learningPathUrl={learningPathUrl}
          updatedAt={updatedAt}
          questionIndex={questionIndex}
          totalQuestions={totalQuestionsCount}
          isStandaloneCourse={isStandaloneCourse}
          scoreCardOnly={scoreCardOnly}
        />

        {!scoreCardOnly && currentQuestion && questionType && (
          <QuestionReview
            question={currentQuestion}
            questionType={questionType}
            questionIndex={questionIndex}
            totalQuestions={totalQuestionsCount}
            quiz={quiz}
            correctAnswers={correctAnswers}
            quizData={quizData}
            result={result}
            showCorrectness={true}
          />
        )}

        <ResultsSummary
          type="quiz"
          totalMarks={totalMarks}
          totalQuestions={totalQuestionsCount}
          percentageAchieved={scorePercentage}
          passingCriteria={assessment?.passing_criteria ?? 75}
          displayName="You"
          attemptNumber={result?.attemptLog?.length ?? 1}
          completedAt={completedAt}
          onRetake={onRetake}
          onNextChapter={onNextChapter}
          nextChapterLabel={nextChapterLabel}
        />
      </div>
    </div>
  );
}
